import { type NavigationGuardWithThis, type RouteRecordRaw } from 'vue-router'
// Dynamic imports for code splitting - all user/auth route components will be lazy loaded
import { useUserStore } from '@/vue/stores/userStore'
import type { Empty } from './types'

const redirectIfAuthenticated: NavigationGuardWithThis<undefined> = async (_, __, next) => {
    const userStore = useUserStore()
    userStore.initializeFromStorage()
    if (userStore.accessToken) {
        await userStore.fetchUser(false)
    }
    if (userStore.accessToken && userStore.userData) {
        next({ name: 'dashboard' })
    } else {
        next()
    }
}

const meta = {
    editMode: false,
    hideSubNavigation: true,
    hideMainNavigation: true,
    excludeFromMainLayout: true,
}

export interface RouteParams {
    'user.profile': Empty
    'user.login': Empty
    'auth.login-with-code': Empty
    'auth.sso': Empty
    'auth.sso-callback': Empty
    'auth.saml2-error': Empty
    'auth.forgot-password': Empty
    'auth.reset-password': { token: string }
    'dashboard': Empty
    'dashboard.legacy': Empty
}

// Auth routes - standalone pages without main layout
const routes: RouteRecordRaw[] = [
    {
        path: '/router/dashboard',
        name: 'dashboard',
        component: () => import('@/vue/pages/dashboard/DashboardPage.vue'),
        meta,
    },
    {
        path: '/dashboard',
        name: 'dashboard.legacy',
        component: () => import('@/vue/pages/dashboard/DashboardPage.vue'),
        meta,
    },
    {
        path: '/router/login',
        name: 'user.login.new',
        component: () => import('@/vue/pages/auth/LoginPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/login',
        name: 'user.login',
        component: () => import('@/vue/pages/auth/LoginPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/router/sso',
        name: 'auth.sso',
        component: () => import('@/vue/pages/auth/LoginSSOPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/sso',
        name: 'auth.sso.new',
        component: () => import('@/vue/pages/auth/LoginSSOPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/router/sso/callback',
        name: 'auth.sso-callback',
        component: () => import('@/vue/pages/auth/SsoCallbackPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/sso/callback',
        name: 'auth.sso-callback.new',
        component: () => import('@/vue/pages/auth/SsoCallbackPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/router/saml2-error',
        name: 'auth.saml2-error',
        component: () => import('@/vue/pages/auth/Saml2ErrorPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/saml2-error',
        name: 'auth.saml2-error.new',
        component: () => import('@/vue/pages/auth/Saml2ErrorPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/router/forgot-password',
        name: 'auth.forgot-password',
        component: () => import('@/vue/pages/auth/ForgotPasswordPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/forgot-password',
        name: 'auth.forgot-password.new',
        component: () => import('@/vue/pages/auth/ForgotPasswordPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/router/reset-password/:token',
        name: 'auth.reset-password',
        component: () => import('@/vue/pages/auth/ResetPasswordPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/reset-password/:token',
        name: 'auth.reset-password.new',
        component: () => import('@/vue/pages/auth/ResetPasswordPage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/router/login-with-code',
        name: 'auth.login-with-code',
        component: () => import('@/vue/pages/auth/LoginWithCodePage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
    {
        path: '/login-with-code',
        name: 'auth.login-with-code.new',
        component: () => import('@/vue/pages/auth/LoginWithCodePage.vue'),
        meta,
        beforeEnter: redirectIfAuthenticated,
    },
]

/**
 * User profile routes for the new layout structure
 */
export const userProfileRoutes: RouteRecordRaw[] = [
    {
        path: 'user',
        component: () => import('@/vue/pages/user/Layout.vue'),
        children: [
            {
                path: '',
                redirect: { name: 'user.profile' },
            },
            {
                path: 'profile',
                name: 'user.profile',
                component: () => import('@/vue/pages/user/ProfileSettingsPage.vue'),
                meta: {
                    title: 'general.profile',
                    redirectsTo: 'company.overview',
                },
            },
        ],
    },
]

export default routes
